import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { cn } from '@Utils';

interface MermaidRendererProps {
  code: string;
}

// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif',
});

const MermaidRenderer = ({ code }: MermaidRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;

      setIsLoading(true);

      try {
        // Generate a unique ID for this diagram
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

        // Clear the container
        containerRef.current.innerHTML = '';

        // Render the diagram
        const { svg } = await mermaid.render(id, code);

        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
          setIsLoading(false);
        }
      } catch (error) {
        setHasError(true);
        setIsLoading(false);
      }
    };

    renderDiagram().catch(() => {
      setHasError(true);
      setIsLoading(false);
    });
  }, [code]);

  // Don't render anything if there's an error
  if (hasError) {
    return null;
  }

  return (
    <div
      className={cn('mermaid-container p-6 rounded-lg overflow-x-auto border border-gray-700', {
        'animate-pulse': isLoading,
        'bg-white': !isLoading,
      })}>
      {isLoading && <div className="min-h-[200px] bg-gray-400 rounded" />}
      <div ref={containerRef} className={isLoading ? 'hidden' : ''} />
    </div>
  );
};

export default MermaidRenderer;
